import { signal } from "@preact/signals";
import type { CartState, GameMode, HeroId, LobbyState, MatchSummary, NetStats, Phase } from "@rivalwatch/shared";

export interface AbilityHud {
  slot: number;
  key: string;
  name: string;
  cdSec: number;
  cdFrac: number;
  charges: number;
  maxCharges: number;
  hasCooldown: boolean;
}

export interface ConvoyHud {
  /** Your team is attacking this round. */
  attacking: boolean;
  /** Payload position, 0..1 of the track. */
  cart: number;
  /** Checkpoint positions, 0..1. */
  checkpoints: number[];
  passed: number;
  clockSec: number;
  state: CartState;
  /** Beside the payload: [friendly, enemy]. */
  pushers: [number, number];
  /** Round two: the first attack's result — the number to beat (or to hold them under). */
  goal?: { frac: number; delivered: boolean; leftSec: number; ours: boolean };
}

export interface MatchHud {
  mode: GameMode;
  phase: Phase;
  phaseSec: number;
  round: number;
  score: [number, number]; // [friendly, enemy]
  progress: [number, number];
  owner: "friend" | "foe" | "none";
  capture: number;
  capTeam: "friend" | "foe" | "none";
  contested: boolean;
  overtime: boolean;
  onPoint: [number, number];
  winner: "friend" | "foe" | "none";
  /** Control on a map built in stages: the name of the stage (sub-map) in play. */
  stageName?: string;
  convoy?: ConvoyHud;
}

export interface RosterEntry {
  id: number;
  name: string;
  hero: HeroId;
  visibleHero?: HeroId;
  form?: number;
  disguised?: boolean;
  friend: boolean;
  alive: boolean;
  respawn: number;
  /** Ultimate charge 0..100 (allies only; 0 for enemies). */
  ult: number;
  me: boolean;
}

export interface HudState {
  hero: HeroId;
  form: number;
  baseHero: HeroId;
  disguised: boolean;
  alive: boolean;
  respawn: number;
  hp: number;
  maxHp: number;
  armor: number;
  maxArmor: number;
  shield: number;
  maxShield: number;
  over: number;
  ammo: number;
  maxAmmo: number;
  reload: number; // 0..1 progress, 0 = not reloading
  abilities: AbilityHud[];
  ult: number;
  ultActive: boolean;
  status: string[];
  resources: { label: string; value: number }[];
  match?: MatchHud;
  inSpawn: boolean;
  /** An aimed ability waiting for LMB (see shared sim/targeting.ts). */
  targeting?: { name: string; verb: string; cancel: boolean };
  /** Who got the Final Hit on you, and who shared the KO. */
  killedBy?: { name: string; hero: HeroId; with: { name: string; hero: HeroId }[] };
  roster: RosterEntry[];
  /** Currently held input buttons (Btn bitmask) — lights up held abilities. */
  buttons: number;
  /** Local view pose for world-relative HUD elements (damage direction). */
  view: { x: number; z: number; yaw: number };
}

export interface FeedItem {
  id: number;
  killer: string;
  killerHero: number;
  victim: string;
  victimHero: number;
  killerFriend: boolean;
  victimFriend: boolean;
  /** Everyone else credited: shared KOs, then assists. */
  helpers: { name: string; hero: number; kind: "ko" | "assist" }[];
  /** You're in it: final hit, shared KO, assist or victim. */
  mine: boolean;
  at: number;
}

/** A KO or assist you just earned, for the prompt beside the crosshair. */
export interface KoNote {
  id: number;
  kind: "final" | "shared" | "assist";
  victim: string;
  victimHero: number;
  /** The teammate who landed the Final Hit (shared KOs and assists). */
  partner?: { name: string; hero: number };
  at: number;
}

export interface ChatLine {
  id: number;
  from: string;
  text: string;
  at: number;
  system: boolean;
}

export type Overlay = "none" | "menu" | "heroes" | "settings";

export const ui = {
  status: signal<"connecting" | "connected" | "disconnected" | "error">("connecting"),
  statusText: signal(""),
  transport: signal<"webrtc" | "ws" | "">(""),
  lobby: signal<LobbyState | undefined>(undefined),
  you: signal(0),
  hud: signal<HudState | undefined>(undefined),
  feed: signal<FeedItem[]>([]),
  chat: signal<ChatLine[]>([]),
  chatOpen: signal(false),
  overlay: signal<Overlay>("menu"),
  scoreboard: signal(false),
  hit: signal<{ at: number; crit: boolean; kill: boolean }>({ at: 0, crit: false, kill: false }),
  hurt: signal(0),
  matchEnd: signal<MatchSummary | undefined>(undefined),
  net: signal<NetStats | undefined>(undefined),
  netGraph: signal(false),
  hitboxes: signal(false),
  kos: signal<KoNote[]>([]),
  /** Rendered hero busts (data URLs), keyed by hero id. */
  portraits: signal<Partial<Record<HeroId, string>>>({}),
  /** Recent hits on the local player: attacker world position, for directional indicators. */
  dmgDirs: signal<{ id: number; at: number; x: number; z: number }[]>([]),
  scoped: signal(false),
  toast: signal<{ text: string; error: boolean; at: number } | undefined>(undefined),
  locked: signal(false),
  fps: signal(0),
};

let lineId = 0;
export function pushChat(from: string, text: string, system = false) {
  ui.chat.value = [...ui.chat.value.slice(-40), { id: ++lineId, from, text, at: Date.now(), system }];
}

export function toast(text: string, error = false) {
  ui.toast.value = { text, error, at: Date.now() };
}
